/* eslint-disable max-len */
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { Row, Col, Form } from 'react-bootstrap'
import InputField from '@components/common/InputField/InputField.component'
import AddressField from '@components/common/AddressField/AddressField.component'
import CustomNumericInputField from '@components/common/CustomNumericInputFileld/CustomNumericInputField.component'
import { APIProvider } from '@vis.gl/react-google-maps'
import { getEnv } from '@utils/env'
import {
  ProviderData,
  ProviderPersonalInfo as ProviderPersonalInfoType,
  ProviderValidationErrors,
} from '@dataTypes/provider.types'
import {
  Gender,
  MaritalStatus,
  Pronoun,
  US_STATE_OPTIONS,
} from '@constants/Providers'
import { useAccountsHeader } from '@pages/Accounts/AccountsHeader.context'
import { useProfile } from '@pages/Accounts/AccountsProfile.context'
import { AccountsSection } from '@constants/Accounts'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import AccountsHeaderActionButton from './AccountsHeaderActionButton.component'
import { areValuesEqual } from '@utils/settings.util'
import './PersonalInfo.component.css'

const GOOGLE_MAPS_API_KEY = getEnv('REACT_APP_GOOGLE_MAPS_API_KEY', '')

type AccountsPersonalForm = Omit<ProviderPersonalInfoType, 'workRole'>

const emptyForm = (): AccountsPersonalForm => ({
  firstName: '',
  lastName: '',
  middleName: '',
  username: '',
  gender: '',
  dateOfBirth: '',
  pronouns: '',
  maritalStatus: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  mobile: '',
  email: '',
  phone: '',
  work: '',
})

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const PERSONAL_COMPARE_KEYS: (keyof AccountsPersonalForm)[] = [
  'firstName',
  'lastName',
  'middleName',
  'gender',
  'dateOfBirth',
  'pronouns',
  'maritalStatus',
  'address',
  'city',
  'state',
  'zip',
  'mobile',
  'email',
  'phone',
  'work',
]

const profileToPersonalForm = (
  profile: ProviderData,
): AccountsPersonalForm => ({
  firstName: profile.firstName ?? '',
  lastName: profile.lastName ?? '',
  middleName: profile.middleName ?? '',
  username: profile.username ?? '',
  gender: profile.gender ?? '',
  dateOfBirth: profile.dateOfBirth ?? '',
  pronouns: profile.pronouns ?? '',
  maritalStatus: profile.maritalStatus ?? '',
  address: profile.address ?? '',
  city: profile.city ?? '',
  state: profile.state ?? '',
  zip: profile.zip ?? '',
  mobile: profile.mobile ?? '',
  email: profile.email ?? '',
  phone: profile.phone ?? '',
  work: profile.work ?? '',
})

const personalToCompareValues = (
  data: AccountsPersonalForm,
): (string | boolean | number)[] =>
  PERSONAL_COMPARE_KEYS.map((key) => data[key] ?? '')

const calcAge = (dateOfBirth: string): number | null => {
  const today = new Date()
  const birthDate = new Date(dateOfBirth)
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--
  }
  return age >= 0 ? age : null
}

const PersonalInfo: React.FC = () => {
  const { setHeaderButtons } = useAccountsHeader()
  const { profile, savePatch, loadState } = useProfile()
  const { showToast } = useNotificationToast()
  const [form, setForm] = useState<AccountsPersonalForm>(emptyForm)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [phoneError, setPhoneError] = useState<{
    mobile?: string
    phone?: string
    work?: string
  }>({})
  const [calculatedAge, setCalculatedAge] = useState<number | null>(null)
  const mobileWrapRef = useRef<HTMLDivElement>(null)
  const addrRef = useRef<{
    addr: string
    city: string
    state: string
    zip: string
  } | null>(null)

  useEffect(() => {
    if (!profile) return
    setForm({
      firstName: profile.firstName ?? '',
      lastName: profile.lastName ?? '',
      middleName: profile.middleName ?? '',
      username: profile.username ?? '',
      gender: profile.gender ?? '',
      dateOfBirth: profile.dateOfBirth ?? '',
      pronouns: profile.pronouns ?? '',
      maritalStatus: profile.maritalStatus ?? '',
      address: profile.address ?? '',
      city: profile.city ?? '',
      state: profile.state ?? '',
      zip: profile.zip ?? '',
      mobile: profile.mobile ?? '',
      email: profile.email ?? '',
      phone: profile.phone ?? '',
      work: profile.work ?? '',
    })
    setCalculatedAge(profile.dateOfBirth ? calcAge(profile.dateOfBirth) : null)
  }, [profile])

  const validationErrors: ProviderValidationErrors = {}

  const handleInputChange = (
    field: string,
    value: string | boolean | number,
  ): void => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  useEffect(() => {
    const last = addrRef.current
    if (!last) return
    if ((form.address || '') !== last.addr) return
    // Keep the last selected address parts applied even if the address input re-renders.
    if (
      (form.city || '') !== last.city ||
      (form.state || '') !== last.state ||
      (form.zip || '') !== last.zip
    ) {
      setForm((prev) => ({
        ...prev,
        city: last.city,
        state: last.state,
        zip: last.zip,
      }))
    }
  }, [form.address, form.city, form.state, form.zip])

  const handleNameBlur = (
    field: keyof AccountsPersonalForm,
    value: string,
  ): void => {
    setForm((prev) => ({ ...prev, [field]: value.trim() }))
  }

  const validateEmail = useCallback((email: string): boolean => {
    if (!email.trim()) {
      setEmailError(null)
      return true
    }
    if (!emailRegex.test(email)) {
      setEmailError('Invalid email address')
      return false
    }
    setEmailError(null)
    return true
  }, [])

  const validatePhone = useCallback(
    (phone: string, field: 'mobile' | 'phone' | 'work'): void => {
      const cleanedPhone = phone.replace(/\D/g, '')
      if (cleanedPhone.length !== 10) {
        if (field === 'mobile' || cleanedPhone.length > 0) {
          setPhoneError((prev) => ({
            ...prev,
            [field]: 'Phone number must be exactly 10 digits',
          }))
        } else {
          setPhoneError((prev) => ({ ...prev, [field]: undefined }))
        }
      } else {
        setPhoneError((prev) => ({ ...prev, [field]: undefined }))
      }
    },
    [],
  )

  const handlePhoneChange = (
    value: string,
    field: 'mobile' | 'phone' | 'work',
  ): void => {
    handleInputChange(field, value)
    validatePhone(value, field)
  }

  const syncMobileFromDomAndValidate = useCallback((): void => {
    const input = mobileWrapRef.current?.querySelector('input')
    const raw = input?.value ?? ''
    const digits = raw.replace(/\D/g, '').slice(0, 10)
    if (digits.length === 10) {
      const current = String(form.mobile ?? '').replace(/\D/g, '')
      if (digits !== current) {
        setForm((prev) => ({ ...prev, mobile: digits }))
      }
      validatePhone(digits, 'mobile')
    } else {
      validatePhone(form.mobile ?? '', 'mobile')
    }
  }, [form.mobile, validatePhone])

  const handleSave = useCallback(async (): Promise<void> => {
    syncMobileFromDomAndValidate()
    if (!validateEmail(form.email)) return
    const ok = await savePatch(AccountsSection.Personal, {
      firstName: form.firstName,
      lastName: form.lastName,
      middleName: form.middleName,
      gender: form.gender,
      dateOfBirth: form.dateOfBirth,
      pronouns: form.pronouns,
      maritalStatus: form.maritalStatus,
      address: form.address,
      city: form.city,
      state: form.state,
      zip: form.zip,
      mobile: form.mobile,
      email: form.email,
      phone: form.phone,
      work: form.work,
    })
    showToast?.(
      ok
        ? createSuccessToast('Saved', 'Personal info updated.')
        : createFailedToast('Error', 'Could not save personal info.'),
    )
  }, [form, savePatch, showToast, syncMobileFromDomAndValidate, validateEmail])

  const handleCancel = useCallback((): void => {
    if (profile) {
      setForm(profileToPersonalForm(profile))
      setCalculatedAge(
        profile.dateOfBirth ? calcAge(profile.dateOfBirth) : null,
      )
    }
    setEmailError(null)
    setPhoneError({})
  }, [profile])

  const hasChanges = useMemo(() => {
    if (!profile) return false
    return !areValuesEqual(
      personalToCompareValues(form),
      personalToCompareValues(profileToPersonalForm(profile)),
    )
  }, [form, profile])

  const actionsDisabled = loadState !== 'ready' || !profile

  useEffect(() => {
    setHeaderButtons(
      <AccountsHeaderActionButton
        onClick={() => {
          if (actionsDisabled || !hasChanges) return
          void handleSave()
        }}
        onCancel={handleCancel}
        cancelDisabled={!hasChanges || actionsDisabled}
        saveDisabled={!hasChanges || actionsDisabled}
      >
        Save changes
      </AccountsHeaderActionButton>,
    )
    return () => setHeaderButtons(null)
  }, [setHeaderButtons, handleSave, handleCancel, hasChanges, actionsDisabled])

  const handleDateOfBirthChange = (value: string): void => {
    handleInputChange('dateOfBirth', value)
    setCalculatedAge(value ? calcAge(value) : null)
  }

  return (
    <div className="accounts-personal-info">
      <Row className="mb-2 gx-2">
        <Col md={4}>
          <InputField
            label="First name"
            placeholder="ex. John"
            value={form.firstName || ''}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            onBlur={(e) => handleNameBlur('firstName', e.target.value)}
          />
        </Col>
        <Col md={4}>
          <InputField
            label="Last name"
            placeholder="ex. Doe"
            value={form.lastName || ''}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            onBlur={(e) => handleNameBlur('lastName', e.target.value)}
          />
        </Col>
        <Col md={4}>
          <Form.Label>
            <span>
              <span>Middle name</span>
              <small className="text-optional"> (Optional)</small>
            </span>
          </Form.Label>
          <InputField
            placeholder="ex. Knox"
            value={form.middleName || ''}
            onChange={(e) => handleInputChange('middleName', e.target.value)}
            onBlur={(e) => handleNameBlur('middleName', e.target.value)}
          />
        </Col>
      </Row>
      <Row className="mb-2 gx-2">
        <Col md={4}>
          <InputField
            label="Username"
            placeholder="Type a username"
            value={form.username || ''}
            disabled
          />
        </Col>
        <Col md={4}>
          <Form.Group controlId="accountsPersonalGender">
            <Form.Label>Gender</Form.Label>
            <Form.Select
              value={form.gender || ''}
              onChange={(e) => handleInputChange('gender', e.target.value)}
            >
              <option value="">Select</option>
              {Object.values(Gender).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4} className="dob-field">
          <div className="personal-dob-row">
            <InputField
              label="Date of birth"
              type="date"
              value={form.dateOfBirth || ''}
              onChange={(e) => handleDateOfBirthChange(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
            />
            {form.dateOfBirth && calculatedAge !== null && (
              <span className="age-display">({calculatedAge} years old)</span>
            )}
          </div>
        </Col>
      </Row>
      <Row className="mb-2 gx-2">
        <Col>
          <Form.Group controlId="accountsPersonalPronouns">
            <Form.Label>
              <span>
                <span>Preferred pronouns</span>
                <small className="text-optional"> (Optional)</small>
              </span>
            </Form.Label>
            <Form.Select
              value={form.pronouns || ''}
              onChange={(e) => handleInputChange('pronouns', e.target.value)}
            >
              <option value="">Select</option>
              {Object.values(Pronoun).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col>
          <Form.Group controlId="accountsPersonalMarital">
            <Form.Label>Marital status</Form.Label>
            <Form.Select
              value={form.maritalStatus || ''}
              onChange={(e) =>
                handleInputChange('maritalStatus', e.target.value)
              }
            >
              <option value="">Select</option>
              {Object.values(MaritalStatus).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-2 gx-2">
        <Col md={12} className="mb-2">
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['places']}>
            <div>
              <AddressField
                label="Address"
                placeholder="Enter an address"
                value={form.address || ''}
                onChange={(value) => handleInputChange('address', value)}
                countries={['US']}
                onSelect={(_place, parsed) => {
                  const addr = parsed.formattedAddress2 ?? ''
                  const city = parsed.city ?? ''
                  const state = parsed.state ?? ''
                  const zip = parsed.postalCode ?? ''
                  addrRef.current = { addr, city, state, zip }
                  handleInputChange('address', addr)
                  handleInputChange('city', city)
                  handleInputChange('state', state)
                  handleInputChange('zip', zip)
                }}
                hint={validationErrors.address || undefined}
                destructive={!!validationErrors.address}
              />
            </div>
          </APIProvider>
        </Col>
      </Row>
      <Row className="mb-2 gx-2 accounts-personal-city-row">
        <Col md={6} className="city-field">
          <InputField
            label="City/town"
            placeholder="Enter a city"
            value={form.city || ''}
            onChange={(e) => handleInputChange('city', e.target.value)}
            hint={validationErrors.city || undefined}
            destructive={!!validationErrors.address}
          />
        </Col>
        <Col md={4}>
          <Form.Group controlId="accountsPersonalState">
            <Form.Label>State</Form.Label>
            <Form.Select
              value={form.state || ''}
              onChange={(e) => handleInputChange('state', e.target.value)}
              className={
                validationErrors.address ? 'form-select-destructive' : ''
              }
            >
              <option value="">--</option>
              {US_STATE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            {validationErrors.state && (
              <div className="hint-text">{validationErrors.state}</div>
            )}
          </Form.Group>
        </Col>
        <Col md={2}>
          <InputField
            label="ZIP"
            value={form.zip || ''}
            onChange={(e) => handleInputChange('zip', e.target.value)}
            hint={validationErrors.zip || undefined}
            destructive={!!validationErrors.address}
          />
        </Col>
      </Row>
      <Row className="mb-2 gx-2">
        <Col>
          <Form.Group>
            <Form.Label>Cellphone</Form.Label>
            <div ref={mobileWrapRef}>
              <CustomNumericInputField
                id="accounts-personal-mobile"
                placeholder="(201) 555-0179"
                value={form.mobile || ''}
                onChange={(value) => handlePhoneChange(value, 'mobile')}
                onBlur={syncMobileFromDomAndValidate}
                autocomplete="section-mobile tel"
                className={phoneError.mobile ? 'input-error' : ''}
                destructive={!!phoneError.mobile}
              />
            </div>
            {phoneError.mobile && (
              <div className="hint-text">{phoneError.mobile}</div>
            )}
          </Form.Group>
        </Col>

        <Col>
          <InputField
            label="Email"
            placeholder="example@email.com"
            type="email"
            value={form.email || ''}
            onChange={(e) => handleInputChange('email', e.target.value)}
            onBlur={(e) => validateEmail(e.target.value)}
            hint={emailError || undefined}
            destructive={!!emailError}
          />
        </Col>
      </Row>
      <Row className="mb-2 gx-2">
        <Col>
          <Form.Group>
            <Form.Label>
              <span>
                <span>Home number</span>
                <small className="text-optional"> (Optional)</small>
              </span>
            </Form.Label>
            <CustomNumericInputField
              id="accounts-personal-home"
              placeholder="(201) 555-0180"
              value={form.phone || ''}
              onChange={(value) => handlePhoneChange(value, 'phone')}
              autocomplete="section-home tel"
              className={phoneError.phone ? 'input-error' : ''}
              destructive={!!phoneError.phone}
            />
            {phoneError.phone && (
              <div className="hint-text">{phoneError.phone}</div>
            )}
          </Form.Group>
        </Col>
        <Col>
          <Form.Group>
            <Form.Label>
              <span>
                <span>Work contact</span>
                <small className="text-optional"> (Optional)</small>
              </span>
            </Form.Label>
            <CustomNumericInputField
              id="accounts-personal-work"
              placeholder="(201) 555-0181"
              value={form.work || ''}
              onChange={(value) => handlePhoneChange(value, 'work')}
              autocomplete="section-work tel"
              className={phoneError.work ? 'input-error' : ''}
              destructive={!!phoneError.work}
            />
            {phoneError.work && (
              <div className="hint-text">{phoneError.work}</div>
            )}
          </Form.Group>
        </Col>
      </Row>
    </div>
  )
}

export default PersonalInfo
